/**
 * Documents register — UI-free rules: type labels and tab groups, search, attention counts, CSV.
 * Everything runs on the list the page already loaded. GET /documents returns at most the newest
 * DOCUMENT_LIST_LIMIT documents; when the list is that long the page says so and also asks the
 * server's ?search= so older documents can still be found.
 */
import { ageDays, formatDate } from '@/lib/format';
import { csvRow } from './csv';

/* Backend type ids → how people write them. Never "Boq" / "Prequal" / "lpo". */
export const DOCUMENT_TYPE_LABELS = {
  drawing: 'Drawing',
  prequal: 'Pre-Qualification',
  material_submittal: 'Material Submittal',
  technical_submittal: 'Technical Submittal',
  costing_sheet: 'Costing Sheet',
  boq: 'BOQ',
  lpo: 'LPO',
  loi: 'LOI',
  shop_drawing: 'Shop Drawing',
  schematic: 'Schematic',
  delivery_note: 'Delivery Note',
  variation: 'Variation',
  other: 'Other',
};

const ACRONYMS = new Set(['boq', 'lpo', 'loi', 'vo', 'dn', 'grn', 'rfi', 'ttl', 'pdf']);

/** 'material_submittal' → 'Material Submittal'; unknown ids are humanised with acronyms kept. */
export function documentTypeLabel(type) {
  const key = String(type || '').trim().toLowerCase();
  if (!key) return 'Document';
  if (DOCUMENT_TYPE_LABELS[key]) return DOCUMENT_TYPE_LABELS[key];
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => (ACRONYMS.has(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

/* Type tabs, in delivery order. A tab with no documents is hidden. Unknown types land in "Other". */
export const TYPE_TABS = [
  { key: 'drawings', label: 'Drawings', types: ['drawing'] },
  { key: 'submittals', label: 'Submittals', types: ['material_submittal', 'technical_submittal'] },
  { key: 'prequal', label: 'Pre-Qualification', types: ['prequal'] },
  { key: 'boq', label: 'BOQ & Costing', types: ['boq', 'costing_sheet'] },
  { key: 'lpo', label: 'LPO / LOI', types: ['lpo', 'loi'] },
  { key: 'shop', label: 'Shop Drawings', types: ['shop_drawing'] },
  { key: 'schematics', label: 'Schematics', types: ['schematic'] },
  { key: 'variations', label: 'Variations', types: ['variation'] },
  { key: 'delivery', label: 'Delivery Notes', types: ['delivery_note'] },
  { key: 'other', label: 'Other', types: ['other'] },
];

const TAB_BY_TYPE = TYPE_TABS.reduce((map, tab) => {
  tab.types.forEach((type) => { map[type] = tab.key; });
  return map;
}, {});

export const typeTabKey = (type) => TAB_BY_TYPE[String(type || '').trim().toLowerCase()] || 'other';

/** GET /api/documents returns at most this many (newest first). */
export const DOCUMENT_LIST_LIMIT = 500;

/** The loaded list plus documents found by a server search (older than the newest 500), no duplicates. */
export function mergeDocuments(docs, extra) {
  if (!extra?.length) return docs || [];
  const seen = new Set((docs || []).map((doc) => doc.id));
  return [...(docs || []), ...extra.filter((doc) => doc?.id && !seen.has(doc.id))];
}

export const hasFile = (doc) => Boolean(String(doc?.file_url || '').trim());
export const isUnlinked = (doc) => !String(doc?.project_name || '').trim();
export const isLatest = (doc) => doc?.is_latest !== false;

/** File kind from the stored file name / url → icon tile tone. */
export function fileKind(doc) {
  if (!hasFile(doc)) return 'none';
  const name = String(doc.file_name || doc.file_url || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'sheet';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return 'image';
  if (String(doc.type || '') === 'variation') return 'variation';
  return 'doc';
}

const PREVIEWABLE = new Set(['pdf', 'png', 'jpg', 'jpeg', 'webp', 'gif', 'txt']);

/** The browser can show it in a tab (PDF, images, text); Excel / Word files are downloaded instead. */
export const isPreviewable = (doc) => hasFile(doc) && PREVIEWABLE.has(fileExtension(doc).toLowerCase());

export function fileExtension(doc) {
  const name = String(doc?.file_name || doc?.file_url || '');
  const dot = name.lastIndexOf('.');
  return dot > -1 ? name.slice(dot + 1).toUpperCase() : '';
}

/** 2_400_000 → '2.4 MB' */
export function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

const normalize = (value) => String(value ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

/** Every word must appear in the title, remarks, project, uploader, type or file name. */
export function matchesSearch(doc, query) {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const haystack = normalize([
    doc.title,
    doc.remarks,
    doc.project_name,
    doc.project_client,
    doc.stage_name,
    doc.uploaded_by,
    documentTypeLabel(doc.type),
    doc.type,
    doc.file_name,
    `v${doc.version || 1}`,
    reviewLabel(reviewStatusOf(doc)),
    REVIEW_PARTIES[doc.review_party] || '',
  ].join(' \u0001 '));
  return words.every((word) => haystack.includes(word));
}

/* Attention filters reachable from the header links. */
export const ATTENTION_FILTERS = {
  no_file: { label: 'Without a file', test: (doc) => !hasFile(doc) },
  unlinked: { label: 'Not linked to a project', test: (doc) => isUnlinked(doc) },
  older: { label: 'Older versions', test: (doc) => !isLatest(doc) },
};

/** Header numbers: counted on the latest version of each document (older ones are reported apart). */
export function summarizeDocuments(docs) {
  const latest = (docs || []).filter(isLatest);
  const projects = new Set(latest.map((doc) => String(doc.project_id || '')).filter(Boolean));
  const linkedProjects = new Set(latest.filter((doc) => !isUnlinked(doc)).map((doc) => doc.project_id));
  return {
    total: latest.length,
    projects: projects.size,
    linkedProjects: linkedProjects.size,
    withoutFile: latest.filter((doc) => !hasFile(doc)).length,
    unlinked: latest.filter(isUnlinked).length,
    older: (docs || []).length - latest.length,
    files: latest.filter(hasFile).length,
  };
}

/** Counts per type tab for the rows that pass every other filter. */
export function countByTab(docs) {
  const counts = { all: 0 };
  (docs || []).forEach((doc) => {
    counts.all += 1;
    const key = typeTabKey(doc.type);
    counts[key] = (counts[key] || 0) + 1;
    // "Awaiting response" (16 idea 8) is across types; the key is only there when something waits.
    if (isAwaitingResponse(doc)) counts[AWAITING_TAB] = (counts[AWAITING_TAB] || 0) + 1;
  });
  return counts;
}

export const SORTS = {
  updated: { label: 'Last updated', compare: (a, b) => String(b.uploaded_at || '').localeCompare(String(a.uploaded_at || '')) },
  title: { label: 'Title A–Z', compare: (a, b) => String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' }) },
  project: {
    label: 'Project',
    compare: (a, b) => {
      // Unlinked documents last, then by project name, newest first inside a project.
      const ua = isUnlinked(a) ? 1 : 0;
      const ub = isUnlinked(b) ? 1 : 0;
      if (ua !== ub) return ua - ub;
      const byName = String(a.project_name || '').localeCompare(String(b.project_name || ''), undefined, { sensitivity: 'base' });
      return byName || String(b.uploaded_at || '').localeCompare(String(a.uploaded_at || ''));
    },
  },
};

/** Applies tab, version mode, attention filter and search; sorted. */
export function filterDocuments(docs, { tab = 'all', versions = 'latest', attention = '', query = '', sort = 'updated' } = {}) {
  const attentionTest = ATTENTION_FILTERS[attention]?.test;
  const rows = (docs || []).filter((doc) => {
    // "Older versions" only makes sense across all versions.
    if (versions === 'latest' && attention !== 'older' && !isLatest(doc)) return false;
    if (attentionTest && !attentionTest(doc)) return false;
    if (tab === AWAITING_TAB) {
      if (!isAwaitingResponse(doc)) return false;
    } else if (tab !== 'all' && typeTabKey(doc.type) !== tab) return false;
    return matchesSearch(doc, query);
  });
  const compare = (SORTS[sort] || SORTS.updated).compare;
  return [...rows].sort(compare);
}

export function documentsToCsv(docs) {
  const header = [
    'Title', 'Type', 'Project', 'Version', 'Status', 'With', 'Status date', 'Latest', 'File', 'Uploaded by', 'Uploaded', 'Remarks',
  ];
  const lines = (docs || []).map((doc) => csvRow([
    doc.title,
    documentTypeLabel(doc.type),
    doc.project_name || 'Unlinked',
    `v${doc.version || 1}`,
    reviewLabel(reviewStatusOf(doc)),
    doc.review_tracked ? (REVIEW_PARTIES[doc.review_party] || '') : '',
    doc.review_tracked ? formatDate(doc.review_date, { fallback: '' }) : '',
    isLatest(doc) ? 'Yes' : 'No',
    hasFile(doc) ? (doc.file_name || 'Attached') : 'No file',
    doc.uploaded_by,
    formatDate(doc.uploaded_at, { fallback: '' }),
    doc.remarks,
  ]));
  return [csvRow(header), ...lines].join('\r\n');
}

/** "AM" for "Awady Mohammed" and the person's colour — shared with every avatar (components/ui/avatar-initials). */
export { avatarColor, initials } from '@/components/ui/avatar-initials';

/* ------------------------------------------------------------ wave 2: stages, versions, filters, upload */

export const LPO_LOI_STAGE = 'LPO/LOI';

/** The delivery stage a document type belongs to (used when a document has no stage of its own). */
export const TYPE_STAGE = {
  drawing: 'Received Drawings',
  prequal: 'Pre-Qualification',
  material_submittal: 'Material Submittal',
  technical_submittal: 'Technical Submittal',
  boq: 'BOQ & Design',
  costing_sheet: 'BOQ & Design',
  lpo: LPO_LOI_STAGE,
  loi: LPO_LOI_STAGE,
  shop_drawing: 'Shop Drawings',
  schematic: 'Schematics',
  variation: 'Variations',
};

/** Types in delivery order, for the Upload drawer's Type field. */
export const TYPE_OPTIONS = [
  'drawing', 'prequal', 'material_submittal', 'technical_submittal', 'boq', 'costing_sheet', 'lpo', 'loi',
  'shop_drawing', 'schematic', 'variation', 'delivery_note', 'other',
].map((value) => ({ value, label: documentTypeLabel(value), stage: TYPE_STAGE[value] || '' }));

/**
 * { name, derived }: the stage the document is filed under, or — when it has none — the stage its type
 * belongs to (derived). The LPO/LOI stage only for people who may see it.
 */
export function documentStage(doc, { canSeeLpoLoi = true } = {}) {
  const own = String(doc?.stage_name || '').trim();
  if (own) return { name: own, derived: false };
  const fromType = TYPE_STAGE[String(doc?.type || '').trim().toLowerCase()] || '';
  if (!fromType || (fromType === LPO_LOI_STAGE && !canSeeLpoLoi)) return { name: '', derived: false };
  return { name: fromType, derived: true };
}

/** "LIV · Dubai Marina" under the project name. */
export function projectSubLine(doc) {
  const client = String(doc?.project_client || doc?.client || '').trim();
  const location = String(doc?.project_location || doc?.location || '').trim();
  return [client, location].filter(Boolean).join(' · ');
}

/** The versions of one document share project + title + type (how POST /documents numbers them). */
export const versionGroupKey = (doc) => [doc?.project_id || '', String(doc?.title || '').trim(), doc?.type || ''].join('\u0001');

const newestVersionFirst = (a, b) => (Number(b.version) || 0) - (Number(a.version) || 0);

/** Every version of the document, newest first. */
export function versionsOf(docs, doc) {
  if (!doc) return [];
  const key = versionGroupKey(doc);
  return (docs || [])
    .filter((item) => versionGroupKey(item) === key)
    .sort(newestVersionFirst);
}

/**
 * Every document's versions in one pass: Map(versionGroupKey → versions, newest first). The page builds it
 * once per list, so each row looks its versions up instead of scanning the whole list (500 rows × 500).
 */
export function groupVersions(docs) {
  const groups = new Map();
  (docs || []).forEach((doc) => {
    const key = versionGroupKey(doc);
    const list = groups.get(key);
    if (list) list.push(doc);
    else groups.set(key, [doc]);
  });
  groups.forEach((list) => list.sort(newestVersionFirst));
  return groups;
}

/** The latest version of the document a new upload with this project / title / type would continue. */
export function findDocumentGroup(docs, { projectId, title, type }) {
  const cleanTitle = String(title || '').trim();
  if (!projectId || !cleanTitle || !type) return null;
  const versions = versionsOf(docs, { project_id: projectId, title: cleanTitle, type });
  return versions.length ? { latest: versions[0], versions, nextVersion: (Number(versions[0].version) || 1) + 1 } : null;
}

/** An existing title that differs only in letter case / spacing (the backend matches titles exactly). */
export function nearTitle(docs, { projectId, title, type }) {
  const wanted = String(title || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!projectId || !wanted || !type) return '';
  const hit = (docs || []).find((doc) => (
    doc.project_id === projectId && doc.type === type
    && String(doc.title || '').trim() !== String(title || '').trim()
    && String(doc.title || '').trim().toLowerCase().replace(/\s+/g, ' ') === wanted
  ));
  return hit ? String(hit.title).trim() : '';
}

/** Titles already used in a project (latest versions), for the Title autocomplete. */
export function titleSuggestions(docs, { projectId, type, query, limit = 6 }) {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  const seen = new Set();
  return (docs || [])
    .filter((doc) => isLatest(doc) && doc.project_id === projectId && (!type || doc.type === type || words.length))
    .filter((doc) => {
      const title = normalize(doc.title);
      return words.every((word) => title.includes(word));
    })
    .sort((a, b) => (a.type === type ? 0 : 1) - (b.type === type ? 0 : 1) || String(a.title).localeCompare(String(b.title)))
    .filter((doc) => {
      const key = versionGroupKey(doc);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}

const TYPE_HINTS = [
  [/\bboq\b|bill of quantit/, 'boq'],
  [/costing/, 'costing_sheet'],
  [/\blpo\b|purchase order/, 'lpo'],
  [/\bloi\b|letter of intent/, 'loi'],
  [/pre ?qual/, 'prequal'],
  [/tech(nical)? ?sub/, 'technical_submittal'],
  [/submittal|\bmsub\b|\bmat sub\b/, 'material_submittal'],
  [/shop ?drawing|\bsd ?\d/, 'shop_drawing'],
  [/schematic/, 'schematic'],
  [/variation|\bvo ?\d/, 'variation'],
  [/delivery ?note|\bdn ?\d/, 'delivery_note'],
  [/drawing|layout|\bplan\b|riser|\bdwg\b|elevation|section/, 'drawing'],
];

/**
 * Suggestions from a dropped file's name: "LIV-Waterside_HVAC-Layout-RevC.pdf" → title "LIV Waterside HVAC
 * Layout RevC", type "drawing", the project whose name is in it. Only suggestions — every field stays editable.
 */
export function guessFromFileName(fileName, projects = []) {
  const base = String(fileName || '').replace(/\.[A-Za-z0-9]{1,5}$/, '');
  const spaced = (/\s/.test(base) ? base : base.replace(/[_-]+/g, ' ')).replace(/\s+/g, ' ').trim();
  const haystack = ` ${normalize(spaced).replace(/[^a-z0-9]+/g, ' ')} `;
  const type = (TYPE_HINTS.find(([pattern]) => pattern.test(haystack)) || [])[1] || '';
  const named = (projects || [])
    .map((project) => ({ project, key: normalize(project.name).replace(/[^a-z0-9]+/g, ' ').trim() }))
    .filter(({ key }) => key.length >= 4 && haystack.includes(` ${key} `))
    .sort((a, b) => b.key.length - a.key.length);
  const sameName = named.filter(({ key }) => key === named[0]?.key);
  return {
    title: spaced.slice(0, 200),
    type,
    projectId: sameName.length === 1 ? sameName[0].project.id : '',
  };
}

/** Project / Stage / Uploaded by filters (16 idea 5). project: an id or UNLINKED. */
export const UNLINKED = '__unlinked__';

export function applyColumnFilters(docs, { project = '', stage = '', uploader = '', canSeeLpoLoi = true } = {}) {
  return (docs || []).filter((doc) => {
    if (project === UNLINKED && !isUnlinked(doc)) return false;
    if (project && project !== UNLINKED && doc.project_id !== project) return false;
    if (stage && documentStage(doc, { canSeeLpoLoi }).name !== stage) return false;
    if (uploader && String(doc.uploaded_by || '').trim() !== uploader) return false;
    return true;
  });
}

/** Options with counts for the three filter chips, from the rows that pass the other filters. */
export function filterOptions(docs, { canSeeLpoLoi = true } = {}) {
  const projects = new Map();
  const stages = new Map();
  const uploaders = new Map();
  let unlinked = 0;
  (docs || []).forEach((doc) => {
    if (isUnlinked(doc)) unlinked += 1;
    else {
      const entry = projects.get(doc.project_id) || { value: doc.project_id, label: doc.project_name, count: 0 };
      entry.count += 1;
      projects.set(doc.project_id, entry);
    }
    const stage = documentStage(doc, { canSeeLpoLoi }).name;
    if (stage) stages.set(stage, (stages.get(stage) || 0) + 1);
    const by = String(doc.uploaded_by || '').trim();
    if (by) uploaders.set(by, (uploaders.get(by) || 0) + 1);
  });
  const stageOrder = Object.values(TYPE_STAGE).filter((value, index, all) => all.indexOf(value) === index);
  return {
    projects: [
      ...[...projects.values()].sort((a, b) => String(a.label).localeCompare(String(b.label))),
      ...(unlinked ? [{ value: UNLINKED, label: 'Not linked to a project', count: unlinked, muted: true }] : []),
    ],
    stages: [...stages.entries()]
      .map(([value, count]) => ({ value, label: value, count }))
      .sort((a, b) => {
        const ia = stageOrder.indexOf(a.value);
        const ib = stageOrder.indexOf(b.value);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.label.localeCompare(b.label);
      }),
    uploaders: [...uploaders.entries()]
      .map(([value, count]) => ({ value, label: value, count }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  };
}

export const plural = (count, singular, pluralWord = `${singular}s`) => `${count} ${count === 1 ? singular : pluralWord}`;

/* ------------------------------------------------------------ wave 3: submittal status, coverage */

/** Where one version stands with the consultant / client (16 idea 8). Tones follow lib/status. */
export const REVIEW_STATUSES = {
  draft: { label: 'Draft', tone: 'gray', hint: 'Not sent yet' },
  submitted: { label: 'Submitted', tone: 'amber', hint: 'Waiting for their reply', party: true },
  approved: { label: 'Approved', tone: 'navy', hint: 'Approved without comments', party: true },
  approved_as_noted: { label: 'Approved as noted', tone: 'navy', hint: 'Approved, with comments to follow', party: true },
  revise_resubmit: { label: 'Revise & resubmit', tone: 'amber', hint: 'Returned — a new version is owed', party: true },
  for_record: { label: 'For record', tone: 'outline', hint: 'Internal or received — no approval needed' },
};
export const REVIEW_ORDER = ['draft', 'submitted', 'approved', 'approved_as_noted', 'revise_resubmit', 'for_record'];
export const REVIEW_PARTIES = { consultant: 'Consultant', client: 'Client' };
/** "Awaiting response": with the other side (Submitted) or back with us (Revise & resubmit). */
export const AWAITING_STATUSES = new Set(['submitted', 'revise_resubmit']);
export const AWAITING_TAB = 'awaiting';
const SUBMITTED_TYPES = new Set(['prequal', 'material_submittal', 'technical_submittal', 'shop_drawing', 'schematic', 'boq', 'variation']);

/** The version's status: as stored, else where its type starts (Draft for submittals, For record otherwise). */
export function reviewStatusOf(doc) {
  const stored = String(doc?.review_status || '').trim();
  if (REVIEW_STATUSES[stored]) return stored;
  return SUBMITTED_TYPES.has(String(doc?.type || '').trim().toLowerCase()) ? 'draft' : 'for_record';
}

export const reviewLabel = (status) => REVIEW_STATUSES[status]?.label || 'Draft';
export const isAwaitingResponse = (doc) => AWAITING_STATUSES.has(reviewStatusOf(doc));

/** A reply up to this many days old is shown without its year ("27 Dec"): it can only be the last one. */
const REPLY_NO_YEAR_DAYS = 330;

/**
 * The line under the status pill: "Client · 3 d" while Submitted (how long they have had it), "Consultant · 3 Feb"
 * for a reply — no year for a reply from the last 11 months, so the line fits the narrow column. Nothing for
 * Draft / For record or a status nobody has set yet. reviewSubTitle() is the full sentence for the tooltip.
 */
export function reviewSubLine(doc, { now } = {}) {
  const status = reviewStatusOf(doc);
  if (!REVIEW_STATUSES[status]?.party || !doc?.review_tracked) return '';
  const party = REVIEW_PARTIES[doc.review_party] || '';
  const day = doc.review_date || doc.submitted_at;
  const days = ageDays(day, { now });
  let when = '';
  if (status === 'submitted') {
    when = days === null || days === undefined ? '' : days <= 0 ? 'today' : `${days} d`;
  } else {
    const recent = typeof days === 'number' && days >= 0 && days <= REPLY_NO_YEAR_DAYS;
    when = formatDate(day, { year: recent ? 'never' : 'auto', now, fallback: '' });
  }
  return [party, when].filter(Boolean).join(' · ');
}

/** "Submitted to the client on 29 Sep 2026 — 3 days ago" / "Approved as noted — the consultant's reply of 27 Dec 2025". */
export function reviewSubTitle(doc, { now } = {}) {
  const status = reviewStatusOf(doc);
  if (!REVIEW_STATUSES[status]?.party || !doc?.review_tracked) return '';
  const party = String(REVIEW_PARTIES[doc.review_party] || '').toLowerCase();
  const day = doc.review_date || doc.submitted_at;
  const date = formatDate(day, { fallback: '' });
  if (status === 'submitted') {
    const days = ageDays(day, { now });
    const age = typeof days !== 'number' ? '' : days <= 0 ? ' — today' : ` — ${plural(days, 'day')} ago`;
    return `Submitted${party ? ` to the ${party}` : ''}${date ? ` on ${date}${age}` : ''}`;
  }
  const reply = party ? `the ${party}'s reply` : 'reply';
  return `${reviewLabel(status)} — ${reply}${date ? ` of ${date}` : ''}`;
}

/** Days a submission has been with the other side (null when the version is not Submitted). */
export function submittedAgeDays(doc, { now } = {}) {
  if (reviewStatusOf(doc) !== 'submitted' || !doc?.review_tracked) return null;
  const days = ageDays(doc.review_date || doc.submitted_at, { now });
  return days === undefined ? null : days;
}

/** The review trail of one version, oldest first: [{ status, label, party, partyLabel, date, note, by, at }]. */
export function reviewTrail(doc) {
  return (Array.isArray(doc?.review_history) ? doc.review_history : [])
    .filter((item) => item && REVIEW_STATUSES[item.status])
    .map((item) => ({ ...item, label: reviewLabel(item.status), partyLabel: REVIEW_PARTIES[item.party] || '' }));
}

/** The 10 delivery stages, in order — the columns of Coverage by stage (16 idea 9). */
export const COVERAGE_STAGES = [
  'Received Drawings', 'Pre-Qualification', 'Material Submittal', 'Technical Submittal', 'BOQ & Design',
  LPO_LOI_STAGE, 'Shop Drawings', 'Schematics', 'Variations', 'Handing over documents',
];
const stageKey = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
// A stage's OWN status decides its cell (not where it sits in the list — stages are not always worked in order):
// finished → Missing without a document; being worked on (or sent back) → Due; Not Started → not reached, except the
// stage the project is at (`current_stage_index`, the Dashboard pipeline's rule) when nothing else is open.
const DONE_STAGE = new Set(['approved', 'completed', 'done']);
const OPEN_STAGE = new Set(['in progress', 'submitted', 'pending', 'rejected']);
const SKIPPED_STAGE = new Set(['not required', 'cancelled', 'canceled']);
const OPEN_PROJECT = new Set(['active', 'on hold']);

/** The coverage cell of one stage without documents: 'skip' | 'missing' | 'due' | 'todo' (see buildCoverage). */
export function coverageGapKind(stage, { isCurrent = false } = {}) {
  const status = String(stage?.status || '').trim().toLowerCase();
  if (SKIPPED_STAGE.has(status)) return 'skip';
  if (DONE_STAGE.has(status)) return 'missing';
  if (OPEN_STAGE.has(status)) return 'due';
  return isCurrent ? 'due' : 'todo';
}

/**
 * Coverage by stage: one row per project, one cell per stage, decided by that stage's own status —
 *   { kind: 'count', count }    documents filed there (latest versions)
 *   { kind: 'due', status }     being worked on (In Progress / Submitted / Rejected), or the Not Started stage the
 *                               project is at when nothing else is open — nothing filed
 *   { kind: 'missing', status } finished (Approved / Completed), nothing filed
 *   { kind: 'todo' }            Not Started · { kind: 'skip' } not required / cancelled · { kind: 'none' } no such stage
 * scope 'open' = Active + On hold projects, 'all' = every project. Rows: projects with documents first, then the
 * biggest gaps (On hold after Active).
 */
export function buildCoverage({ docs, projects, canSeeLpoLoi = true, scope = 'open' }) {
  const columns = COVERAGE_STAGES.filter((name) => canSeeLpoLoi || name !== LPO_LOI_STAGE);
  const latest = (docs || []).filter((doc) => isLatest(doc));
  const counts = new Map();
  latest.forEach((doc) => {
    if (isUnlinked(doc)) return;
    const stage = documentStage(doc, { canSeeLpoLoi }).name;
    if (!stage) return;
    const key = `${doc.project_id}|${stageKey(stage)}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const inScope = (projects || []).filter((project) => (
    scope === 'all' || OPEN_PROJECT.has(String(project.status || '').trim().toLowerCase())
  ));
  const rows = inScope.map((project) => {
    const stages = Array.isArray(project.stages) ? project.stages : [];
    const current = Number.isInteger(project.current_stage_index) ? project.current_stage_index : -1;
    const cells = {};
    let filed = 0;
    let missing = 0;
    let due = 0;
    columns.forEach((name) => {
      const index = stages.findIndex((stage) => stageKey(stage.name) === stageKey(name));
      const count = counts.get(`${project.id}|${stageKey(name)}`) || 0;
      filed += count;
      if (count) {
        cells[name] = { kind: 'count', count };
        return;
      }
      if (index === -1) {
        cells[name] = { kind: 'none' };
        return;
      }
      const kind = coverageGapKind(stages[index], { isCurrent: index === current });
      if (kind === 'due' || kind === 'missing') {
        cells[name] = { kind, status: String(stages[index].status || '').trim() || 'Not Started' };
        if (kind === 'due') due += 1;
        else missing += 1;
      } else {
        cells[name] = { kind };
      }
    });
    const onHold = String(project.status || '').trim().toLowerCase() === 'on hold';
    return { project, cells, filed, missing, due, onHold };
  });
  rows.sort((a, b) => (
    Number(b.filed > 0) - Number(a.filed > 0)
    || Number(a.onHold) - Number(b.onHold)
    || b.missing - a.missing
    || b.filed - a.filed
    || b.due - a.due
    || String(a.project.name || '').localeCompare(String(b.project.name || ''))
  ));
  return {
    columns,
    rows,
    withoutDocuments: rows.filter((row) => row.filed === 0).length,
    unlinked: latest.filter(isUnlinked).length,
  };
}

/** "Now: Material Submittal" / "On hold · Received Drawings" / "All stages done" under the project name. */
export function coverageSubLine(project) {
  const onHold = String(project?.status || '').trim().toLowerCase() === 'on hold';
  const stage = String(project?.current_stage || '').trim();
  const where = stage ? stage : (project?.stages || []).length ? 'All stages done' : 'No stages';
  return onHold ? `On hold · ${where}` : stage ? `Now: ${stage}` : where;
}
